import { FLOWS_ENUM, PAYMENT_METHODS } from 'src/common/enums';
import { getPaymentCategory } from 'src/common/paymentUtils';
import {
  CertifiedPaymentsPayload,
  FullPayPayload,
  GenericProviderPayload,
  HeartlandPayload,
  KalaPayload,
  PAYMENT_PROVIDER,
  PaymentMethod,
  RegistrantPayload,
} from 'src/types/payment';
import { Person } from 'src/types/person';
import { PaymentPeriod } from 'src/types/tax';

const buildRegistrantPayload = (
  person: Person | undefined
): RegistrantPayload | undefined => {
  if (!person) return undefined;
  return {
    first_name: person.firstName,
    last_name: person.lastName,
    email: person.email,
    address: person.mailingAddress,
    phone: person.phone,
  };
};

const buildCertifiedPaymentsPayload = (
  person: Person | undefined,
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string,
  totalDue: number | string
): CertifiedPaymentsPayload => {
  const registrant = buildRegistrantPayload(person);
  const fee_type = getPaymentCategory(flow)!;

  if (selectedLicenses.length > 0) {
    const payment_periods = selectedLicenses.flatMap((period) =>
      period.licenses.map((license) => ({
        license: license.licenseNumber ?? license.license ?? permitIdentifier,
        formatted_period: period.name,
        amount: period.totalDueForPeriod ?? Number(totalDue),
        fee_type,
      }))
    );
    return { registrant, payment_periods };
  }

  return {
    registrant,
    payment_periods: [
      {
        license: permitIdentifier,
        formatted_period: '',
        amount: Number(totalDue),
        fee_type,
      },
    ],
  };
};

const buildHeartlandPayload = (
  person: Person | undefined
): HeartlandPayload => ({
  registrant: buildRegistrantPayload(person),
});

const buildFullPayPayload = (
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string
): FullPayPayload => {
  if (flow !== FLOWS_ENUM.PAY_TOT || selectedLicenses.length === 0) {
    return { payment_periods: [] };
  }

  const payment_periods = selectedLicenses.flatMap((period) =>
    period.licenses.map((license) => ({
      license_id: license.license ?? license.applicationNumber,
      license_number:
        license.licenseNumber ??
        license.license ??
        license.applicationNumber ??
        permitIdentifier,
      formatted_period: period.name,
    }))
  );
  return { payment_periods };
};

const buildKalaPayload = (paymentMethod: PaymentMethod): KalaPayload => ({
  payment_method:
    paymentMethod === PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD
      ? 'credit_card'
      : 'ach',
});

export const buildGenericProviderPayload = (
  provider: PAYMENT_PROVIDER | undefined,
  person: Person | undefined,
  selectedLicenses: PaymentPeriod[],
  flow: FLOWS_ENUM,
  permitIdentifier: string,
  totalDue: number | string,
  paymentMethod: PaymentMethod
): GenericProviderPayload => {
  switch (provider) {
    case PAYMENT_PROVIDER.CERTIFIED_PAYMENTS:
      return buildCertifiedPaymentsPayload(
        person,
        selectedLicenses,
        flow,
        permitIdentifier,
        totalDue
      );
    case PAYMENT_PROVIDER.HEARTLAND:
      return buildHeartlandPayload(person);
    case PAYMENT_PROVIDER.FULLPAY:
      return buildFullPayPayload(selectedLicenses, flow, permitIdentifier);
    case PAYMENT_PROVIDER.KALA:
      return buildKalaPayload(paymentMethod);
    default:
      return null;
  }
};
